import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { useQuery } from 'react-query';
import { teacherModuleAPI } from '../../services/api';
import { LabelWithInfo } from '../InfoTooltip';
import { formatLeaveDate } from './LeaveRequestDetails';
import SubstituteTeacherPicker, { parseSubstituteTeachersResponse } from './SubstituteTeacherPicker';
import MergeBatchPicker from './MergeBatchPicker';

const SAVED_MODES = new Set(['substitute', 'merge', 'cancel', 'none']);

function initialModeForBatch(batch) {
  return SAVED_MODES.has(batch.resolutionType) ? batch.resolutionType : 'substitute';
}

function initialSubstituteForBatch(batch) {
  if (batch.resolutionType === 'substitute') return batch.substituteTeacherId || '';
  if (!batch.resolutionType) return batch.substituteTeacherId || batch.secondaryTeacherId || '';
  return batch.substituteTeacherId || '';
}

function unresolvedHint(batch, mode, substituteId, mergeTargetId) {
  if (mode === 'substitute' && !substituteId) {
    return `Pick a substitute for ${batch.name}, or switch to Merge, Cancel, or No action.`;
  }
  if (mode === 'merge' && !mergeTargetId) {
    return `Pick a merge target for ${batch.name}, or switch to another action.`;
  }
  return `Choose an action for ${batch.name}.`;
}

function BatchResolutionBlock({
  batch,
  leaveDate,
  mode,
  onModeChange,
  substituteId,
  onSubstituteChange,
  mergeTargetId,
  onMergeTargetChange,
  updateSecondary,
  onUpdateSecondaryChange,
  incomplete = false,
  incompleteHint = '',
}) {
  const substitutesQuery = useQuery(
    ['availableSubstitutes', batch.id, batch.sessionId, leaveDate],
    async () => {
      if (batch.sessionId) {
        const res = await teacherModuleAPI.getSessionAvailableSubstitutes(batch.sessionId);
        return parseSubstituteTeachersResponse(res);
      }
      const res = await teacherModuleAPI.getBatchAvailableSubstitutes(batch.id, leaveDate);
      return parseSubstituteTeachersResponse(res);
    },
    { enabled: mode === 'substitute' && !!leaveDate }
  );

  const { available: teachers, unavailable, recentSubstitutions = [] } = substitutesQuery.data || {
    available: [],
    unavailable: [],
    recentSubstitutions: [],
  };

  return (
    <Stack
      spacing={1}
      sx={{
        mb: 2,
        p: 2,
        border: 1,
        borderColor: incomplete ? 'warning.main' : 'divider',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">{batch.name}</Typography>
        {incomplete ? (
          <Chip size="small" color="warning" label="Needs a decision" />
        ) : (
          <Chip size="small" color="success" variant="outlined" label="Ready" />
        )}
      </Stack>
      {incomplete && incompleteHint ? (
        <Alert severity="warning" sx={{ py: 0 }}>
          {incompleteHint}
        </Alert>
      ) : null}
      {batch.startTime && batch.endTime ? (
        <Typography variant="caption" color="text.secondary">
          Class time: {String(batch.startTime).slice(0, 5)}–{String(batch.endTime).slice(0, 5)}
        </Typography>
      ) : null}

      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => v && onModeChange(v)}
        sx={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}
      >
        <ToggleButton value="substitute">
          {substituteId ? 'Change substitute' : 'Assign substitute'}
        </ToggleButton>
        <ToggleButton value="merge" disabled={!batch.sessionId}>
          Merge batch
        </ToggleButton>
        <ToggleButton value="cancel">Cancel class</ToggleButton>
        <ToggleButton value="none">No action</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'none' ? (
        <Alert severity="info" sx={{ py: 0 }}>
          Leave is approved for this batch with no coverage change now. Handle substitute assignment or class
          cancellation separately later.
        </Alert>
      ) : null}

      {mode === 'cancel' ? (
        <Alert severity="info" sx={{ py: 0 }}>
          This class will be cancelled for the leave day. No substitute is required. Use when no cover teacher
          is available.
        </Alert>
      ) : null}

      {mode === 'substitute' ? (
        <>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <LabelWithInfo
              label="Substitute teacher"
              title="Cover teacher for this batch"
              body="Teachers are grouped as available or override. Available teachers fit working hours with no conflicts. Supervisors may still assign override teachers (busy, outside hours, or on leave) with a warning."
            />
          </Typography>
          {substitutesQuery.isLoading ? (
            <CircularProgress size={22} />
          ) : (
            <SubstituteTeacherPicker
              available={teachers}
              unavailable={unavailable}
              loading={false}
              value={substituteId}
              onChange={onSubstituteChange}
              recentSubstitutions={recentSubstitutions}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox checked={!!updateSecondary} onChange={(e) => onUpdateSecondaryChange(e.target.checked)} />
            }
            label={
              <LabelWithInfo
                label="Also update batch secondary (shadow)"
                title="Standing backup teacher"
                body="Saves this teacher as the batch secondary for future leave."
              />
            }
          />
        </>
      ) : null}

      {mode === 'merge' ? (
        !batch.sessionId ? (
          <Alert severity="info" sx={{ py: 0 }}>
            No session scheduled yet — use assign substitute instead.
          </Alert>
        ) : (
          <MergeBatchPicker
            sessionId={batch.sessionId}
            value={mergeTargetId || ''}
            onChange={onMergeTargetChange}
          />
        )
      ) : null}
    </Stack>
  );
}

export default function LeaveApprovalDialog({
  open,
  onClose,
  request,
  onConfirm,
  isSubmitting,
  coverageOnly = false,
}) {
  const [decisionNotes, setDecisionNotes] = useState('');
  const [modeByBatch, setModeByBatch] = useState({});
  const [substitutesByBatch, setSubstitutesByBatch] = useState({});
  const [mergeTargetByBatch, setMergeTargetByBatch] = useState({});
  const [updateSecondaryByBatch, setUpdateSecondaryByBatch] = useState({});

  const batchesQuery = useQuery(
    ['leaveApprovalBatches', request?.id],
    async () => {
      const res = await teacherModuleAPI.getLeaveApprovalBatches(request.id);
      return res.data?.data || [];
    },
    { enabled: open && !!request?.id }
  );

  const affectedBatches = batchesQuery.data || [];
  const leaveDate = request?.leaveDate;

  useEffect(() => {
    if (!open || !batchesQuery.data?.length) return;
    const modes = {};
    const subs = {};
    const merges = {};
    batchesQuery.data.forEach((b) => {
      modes[b.id] = initialModeForBatch(b);
      subs[b.id] = initialSubstituteForBatch(b);
      if (b.resolutionType === 'merge' && b.mergedIntoSessionId) {
        merges[b.id] = b.mergedIntoSessionId;
      }
    });
    setModeByBatch(modes);
    setSubstitutesByBatch(subs);
    setMergeTargetByBatch(merges);
    setUpdateSecondaryByBatch({});
    setDecisionNotes('');
  }, [open, request?.id, batchesQuery.data]);

  const buildCoveragePayload = () => {
    const batchSubstitutes = [];
    const batchMerges = [];
    const batchCancellations = [];
    const batchNoActions = [];
    affectedBatches.forEach((b) => {
      const mode = modeByBatch[b.id] || 'substitute';
      if (mode === 'none') {
        batchNoActions.push({ batchId: b.id });
      } else if (mode === 'cancel') {
        batchCancellations.push({ batchId: b.id });
      } else if (mode === 'merge' && mergeTargetByBatch[b.id]) {
        batchMerges.push({ batchId: b.id, targetSessionId: mergeTargetByBatch[b.id] });
      } else if (mode === 'substitute' && substitutesByBatch[b.id]) {
        batchSubstitutes.push({
          batchId: b.id,
          substituteTeacherId: substitutesByBatch[b.id],
          updateBatchSecondary: !!updateSecondaryByBatch[b.id],
        });
      }
    });
    return { batchSubstitutes, batchMerges, batchCancellations, batchNoActions };
  };

  const batchHasCompleteResolution = (b) => {
    const mode = modeByBatch[b.id] || 'substitute';
    if (mode === 'cancel' || mode === 'none') return true;
    if (mode === 'merge') return !!mergeTargetByBatch[b.id];
    if (mode === 'substitute') return !!substitutesByBatch[b.id];
    return false;
  };

  const unresolvedBatches = affectedBatches.filter((b) => !batchHasCompleteResolution(b));
  const hasAnyResolvableCoverage = affectedBatches.some((b) => batchHasCompleteResolution(b));
  const allBatchesResolved = unresolvedBatches.length === 0;

  const handleApprove = () => {
    onConfirm({
      status: 'approved',
      decisionNotes,
      ...buildCoveragePayload(),
    });
  };

  const handleSaveCoverage = () => {
    onConfirm(buildCoveragePayload());
  };

  const saveDisabled =
    isSubmitting || (coverageOnly && affectedBatches.length > 0 && !hasAnyResolvableCoverage);
  const approveDisabled =
    isSubmitting || (!coverageOnly && affectedBatches.length > 0 && !allBatchesResolved);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {coverageOnly ? 'Assign batch coverage' : 'Approve leave — cover or cancel affected classes'}
      </DialogTitle>
      <DialogContent>
        {request ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {request.teacherName || 'Teacher'} — {formatLeaveDate(request.leaveDate)}
          </Typography>
        ) : null}

        {!coverageOnly ? (
          <TextField
            fullWidth
            size="small"
            label="Decision notes (optional)"
            value={decisionNotes}
            onChange={(e) => setDecisionNotes(e.target.value)}
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
        ) : null}

        {batchesQuery.isLoading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : null}

        {!batchesQuery.isLoading && affectedBatches.length === 0 ? (
          <Alert severity="info">
            {coverageOnly
              ? 'No batches scheduled on this leave date.'
              : 'No batches scheduled on this leave date. You can approve without coverage.'}
          </Alert>
        ) : null}

        {!batchesQuery.isLoading && affectedBatches.length > 0 && coverageOnly ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Save coverage for the batches you have ready now. You can return later for remaining classes.
            {unresolvedBatches.length > 0
              ? ` Still open: ${unresolvedBatches.map((b) => b.name).join(', ')}.`
              : ''}
          </Alert>
        ) : null}

        {!batchesQuery.isLoading && affectedBatches.length > 0 && !coverageOnly && unresolvedBatches.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Approve is waiting on {unresolvedBatches.map((b) => b.name).join(', ')}. For each of those classes,
            pick a substitute, merge, cancel, or No action — No action counts as complete.
          </Alert>
        ) : null}

        {!batchesQuery.isLoading && affectedBatches.length > 0 && !coverageOnly && unresolvedBatches.length === 0 ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Every class has a decision, including No action. You can approve this leave.
          </Alert>
        ) : null}

        {!batchesQuery.isLoading
          ? affectedBatches.map((b) => (
              <BatchResolutionBlock
                key={b.id}
                batch={b}
                leaveDate={leaveDate}
                mode={modeByBatch[b.id] || 'substitute'}
                onModeChange={(v) => setModeByBatch((prev) => ({ ...prev, [b.id]: v }))}
                substituteId={substitutesByBatch[b.id]}
                onSubstituteChange={(v) => setSubstitutesByBatch((prev) => ({ ...prev, [b.id]: v }))}
                mergeTargetId={mergeTargetByBatch[b.id]}
                onMergeTargetChange={(v) => setMergeTargetByBatch((prev) => ({ ...prev, [b.id]: v }))}
                updateSecondary={!!updateSecondaryByBatch[b.id]}
                onUpdateSecondaryChange={(v) =>
                  setUpdateSecondaryByBatch((prev) => ({ ...prev, [b.id]: v }))
                }
                incomplete={!batchHasCompleteResolution(b)}
                incompleteHint={unresolvedHint(
                  b,
                  modeByBatch[b.id] || 'substitute',
                  substitutesByBatch[b.id],
                  mergeTargetByBatch[b.id]
                )}
              />
            ))
          : null}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
        {!coverageOnly && unresolvedBatches.length > 0 ? (
          <Alert severity="warning">
            Cannot approve yet. Finish: {unresolvedBatches.map((b) => b.name).join(', ')}.
          </Alert>
        ) : null}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={coverageOnly ? 'primary' : 'success'}
            disabled={saveDisabled || approveDisabled}
            onClick={coverageOnly ? handleSaveCoverage : handleApprove}
          >
            {isSubmitting
              ? coverageOnly
                ? 'Saving…'
                : 'Approving…'
              : coverageOnly
                ? 'Save coverage'
                : 'Approve'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
